import { Download, FileSpreadsheet, FileText, PackageSearch } from "lucide-react";
import { useMemo, useState } from "react";

import { EmptyState } from "../components/EmptyState";
import { monthPeriod } from "../lib/report-period";
import { trpc } from "../lib/trpc";
import { PURCHASE_ORDER_STATUS_LABELS as statusLabels } from "../../shared/purchase-order-status";
import { normalizeTimeZone } from "../../shared/time-zone";
import { MATERIAL_UNIT_PLURAL_LABELS } from "../../shared/material-units";

const unitLabels = MATERIAL_UNIT_PLURAL_LABELS;

type OrderStatus = keyof typeof statusLabels;

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

type ReportFilters = {
  from: string;
  to: string;
  supplierId: string;
  materialId: string;
  siteId: string;
  status: "" | OrderStatus;
  timeZone: string;
};

function exportUrl(format: "csv" | "pdf", filters: ReportFilters) {
  const query = new URLSearchParams({
    from: filters.from,
    to: filters.to,
    timeZone: filters.timeZone,
  });
  if (filters.supplierId) query.set("supplierId", filters.supplierId);
  if (filters.materialId) query.set("materialId", filters.materialId);
  if (filters.siteId) query.set("siteId", filters.siteId);
  if (filters.status) query.set("status", filters.status);
  return `/api/purchase-reports/monthly.${format}?${query}`;
}

export function PurchaseReportPage() {
  const [month, setMonth] = useState(currentMonth);
  const [supplierId, setSupplierId] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [status, setStatus] = useState<"" | OrderStatus>("");
  const timeZone = normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const period = monthPeriod(month);
  const filters = useMemo<ReportFilters>(() => ({ ...period, supplierId, materialId, siteId, status, timeZone }), [materialId, period.from, period.to, siteId, status, supplierId, timeZone]);
  const suppliers = trpc.suppliers.list.useQuery();
  const materials = trpc.materials.list.useQuery();
  const sites = trpc.sites.list.useQuery();
  const report = trpc.purchasing.reports.monthly.useQuery({
    from: filters.from,
    to: filters.to,
    supplierId: filters.supplierId || null,
    materialId: filters.materialId || null,
    siteId: filters.siteId || null,
    status: filters.status || null,
  }, { enabled: Boolean(filters.from && filters.to) });

  return (
    <section className="purchase-page">
      <header className="page-heading">
        <div><p className="eyebrow">Quantità, non contabilità</p><h1>Riepilogo materiali</h1><p>Controlla mensilmente cosa è stato ordinato, ricevuto e utilizzato.</p></div>
        <div className="page-heading__actions report-downloads">
          <a className="button button--outline" href={exportUrl("csv", filters)}><FileSpreadsheet size={18} /> Esporta CSV</a>
          <a className="button button--primary" href={exportUrl("pdf", filters)}><FileText size={18} /> Esporta PDF</a>
        </div>
      </header>

      <section className="report-filters">
        <label><span>Mese</span><input aria-label="Mese" type="month" value={month} onChange={(event) => setMonth(event.target.value)} /></label>
        <label><span>Fornitore</span><select value={supplierId} onChange={(event) => setSupplierId(event.target.value)}><option value="">Tutti</option>{suppliers.data?.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}</select></label>
        <label><span>Materiale</span><select value={materialId} onChange={(event) => setMaterialId(event.target.value)}><option value="">Tutti</option>{materials.data?.map((material) => <option key={material.id} value={material.id}>{material.name}</option>)}</select></label>
        <label><span>Struttura</span><select value={siteId} onChange={(event) => setSiteId(event.target.value)}><option value="">Tutte</option>{sites.data?.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>
        <label><span>Stato ordine</span><select value={status} onChange={(event) => setStatus(event.target.value as typeof status)}><option value="">Tutti</option>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      </section>

      {report.error && <div className="query-error" role="alert"><strong>Riepilogo non disponibile</strong><span>{report.error.message}</span><button className="button button--outline" type="button" onClick={() => void report.refetch()}>Riprova</button></div>}

      {!report.error && !report.isLoading && <><div className="report-summary"><PackageSearch size={22} /><span><strong>{report.data?.length ?? 0}</strong> combinazioni di fornitore, materiale e struttura</span><Download size={19} /></div>

      <div className="report-table-wrap">
        <table className="report-table">
          <thead><tr><th>Fornitore</th><th>Materiale</th><th>Struttura</th><th>Stato</th><th>Ordinato</th><th>Ricevuto</th><th>Utilizzato</th></tr></thead>
          <tbody>{report.data?.map((row) => <tr key={`${row.supplierId}-${row.materialId}-${row.siteId}-${row.status}`}><td>{row.supplierName}</td><td>{row.materialName}</td><td>{row.siteName}</td><td><span className={`request-status request-status--${row.status}`}>{statusLabels[row.status]}</span></td><td>{row.orderedQuantity} {unitLabels[row.unit]}</td><td>{row.receivedQuantity} {unitLabels[row.unit]}</td><td>{row.usedQuantity} {unitLabels[row.unit]}</td></tr>)}</tbody>
        </table>
      </div></>}
      {!report.error && !report.isLoading && !report.data?.length && <EmptyState title="Nessun movimento nel periodo">Prova un altro mese oppure modifica i filtri.</EmptyState>}
      {report.isLoading && <p className="loading-copy">Caricamento riepilogo...</p>}
    </section>
  );
}
